import { useState } from "react";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2, Clock, Heart } from "lucide-react";
import { useReveal } from "@/lib/useReveal";

const REAL_EMAIL = "Info.cleanwaterdrop@gmail.com";

export default function Contact() {
  useReveal();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success("Thank you — we'll get back to you within 48 hours.");
      setDone(true);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="ripple-bg">
        <div className="container-app pt-24 sm:pt-32 pb-20">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 reveal">
              <p className="eyebrow">Get in touch</p>
              <h1 className="font-heading mt-6 text-[44px] sm:text-[64px] lg:text-[76px] leading-[0.98] tracking-[-0.02em] font-semibold text-[#0A2540]">
                We'd love to{" "}
                <span className="text-[#2E8B8B]">hear from you.</span>
              </h1>
            </div>
            <div className="lg:col-span-4 reveal">
              <p className="text-lg text-slate-600 leading-relaxed">
                Whether you're seeking support, looking to partner, volunteer, donate, or simply
                learn more — we read every message with care.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="container-app pb-24">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5 space-y-5 reveal">
            <div className="card-soft">
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#E6F0F2] text-[#2E8B8B]">
                <Mail className="h-5 w-5" />
              </span>
              <p className="eyebrow mt-6">Email us</p>
              <a
                href={`mailto:${REAL_EMAIL}`}
                className="font-heading mt-2 block text-xl text-[#0A2540] hover:text-[#2E8B8B] transition-colors break-all"
                data-testid="contact-email-link"
              >
                {REAL_EMAIL}
              </a>
              <p className="mt-2 text-sm text-slate-500">We reply within 48 hours, Monday to Friday.</p>
            </div>

            <div className="card-soft">
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#E6F0F2] text-[#2E8B8B]">
                <Heart className="h-5 w-5" />
              </span>
              <p className="eyebrow mt-6">Looking for support?</p>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                If you're in need of shelter, food, hygiene services, or someone to talk to —
                please reach out. You don't need to share anything you're not ready to. We meet
                you where you are.
              </p>
            </div>

            <div className="card-soft">
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#E6F0F2] text-[#2E8B8B]">
                <Clock className="h-5 w-5" />
              </span>
              <p className="eyebrow mt-6">In a crisis?</p>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                If you or someone you know is in immediate danger, please call your local
                emergency services first. For non-urgent support, our team will respond as
                quickly as we can.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            {done ? (
              <div className="card-soft text-center py-16" data-testid="contact-success">
                <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#E6F0F2] text-[#2E8B8B]">
                  <CheckCircle2 className="h-8 w-8" />
                </span>
                <h3 className="font-heading mt-6 text-2xl text-[#0A2540]">Message received.</h3>
                <p className="mt-3 text-base text-slate-600 max-w-md mx-auto">
                  Thank you for reaching out. A member of our team will respond within 48 hours.
                </p>
                <button onClick={() => setDone(false)} className="btn-ghost mt-6" data-testid="contact-reset">
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="card-soft space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-[0.22em] text-slate-500">Full name</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="input-field mt-2"
                      placeholder="Your name"
                      data-testid="contact-name-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.22em] text-slate-500">Email</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="input-field mt-2"
                      placeholder="you@email.com"
                      data-testid="contact-email-input"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-[0.22em] text-slate-500">Subject</label>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="input-field mt-2"
                    placeholder="How can we help?"
                    data-testid="contact-subject-input"
                  />
                </div>

                <div>
                  <label className="text-xs uppercase tracking-[0.22em] text-slate-500">Your message</label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={6}
                    className="input-field mt-2 resize-none"
                    placeholder="Share as much or as little as you'd like."
                    data-testid="contact-message-input"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full disabled:opacity-70"
                  data-testid="contact-submit-button"
                >
                  <Send className="h-4 w-4" />
                  {submitting ? "Sending…" : "Send message"}
                </button>

                <p className="text-center text-xs text-slate-500">
                  Your message is private. We never share or sell your information.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
